import React from 'react';
import clsx from 'clsx';
import {
  ContentChunkFragment,
  TileFragmentFragment,
} from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { extractComponentFromChunk } from '@/ui/utils/tiles';
// eslint-disable-next-line import/no-cycle -- container tile renders tiles; safe (referenced at render)
import TilesArrayRenderer from '@/ui/components/tileRenderers/PageGridRenderer/tiles-array-renderer';

const WIDTH_COMPONENT_ID = 'width';
const TILES_COMPONENT_ID = 'tiles';

// Width preset -> 12-grid span (literal classes for Tailwind; unknown/unset -> full).
const WIDTH_TO_COL_SPAN: Record<string, string> = {
  full: 'lg:col-span-12',
  '1/2': 'lg:col-span-6',
  '1/3': 'lg:col-span-4',
  '2/3': 'lg:col-span-8',
  '1/4': 'lg:col-span-3',
  '3/4': 'lg:col-span-9',
};

const DEFAULT_COL_SPAN = WIDTH_TO_COL_SPAN.full;

/** Parsed `split-layout` tile: one repeatable `columns` chunk, each column a `width` + `tiles`. */
export interface SplitLayoutTile {
  columns?: ContentChunkFragment;
}

interface SplitLayoutProps {
  tile: SplitLayoutTile;
}

/** Container tile: a 12-col grid of columns sized by their `width` preset. Stacks below `lg`. */
export function SplitLayout({ tile }: Readonly<SplitLayoutProps>) {
  const columns = tile.columns?.chunks ?? [];

  return (
    <div className="mx-auto grid w-full max-w-screen-2xl grid-cols-1 px-3 sm:px-8 lg:grid-cols-12">
      {columns.map((chunk) => {
        const width = extractComponentFromChunk(chunk, WIDTH_COMPONENT_ID, 'SelectionContent')
          ?.options?.[0]?.value;

        const columnTiles = (extractComponentFromChunk(
          chunk,
          TILES_COMPONENT_ID,
          'ItemRelationsContent',
        )?.items ?? []) as TileFragmentFragment[];

        // Key by the column's tile ids (stable, unique); empty column falls back to its width.
        const columnKey =
          columnTiles.map((columnTile) => columnTile.id).join('-') || `empty-${width ?? 'full'}`;

        return (
          <div
            key={columnKey}
            className={clsx(
              WIDTH_TO_COL_SPAN[width ?? ''] ?? DEFAULT_COL_SPAN,
              // Consistent per-tile inset so mixed tiles align (Container tiles zero their own px).
              'space-y-4 [&>*]:px-4 [&>*>.max-w-screen-2xl]:!px-0 sm:space-y-6 sm:[&>*]:px-6',
            )}
          >
            <TilesArrayRenderer tiles={columnTiles} />
          </div>
        );
      })}
    </div>
  );
}
